import {
    CreateInternalSupportMessagePayload,
    CreateInternalSupportTicketPayload,
    InternalSupportActivityListResponse,
    InternalSupportMessage,
    InternalSupportMessageListResponse,
    InternalSupportTicket,
    InternalSupportTicketListParams,
    InternalSupportTicketListResponse,
    InternalSupportUser,
    TicketPriority,
    TicketStatus,
    UpdateInternalSupportTicketPayload,
} from '../helpers/internal-support.types';
import http from 'httpConfig/http';
import { getResellerIdFromRedux } from 'modules/shared/helpers/shared.utils';

const getBaseUrl = () => `${import.meta.env.VITE_APP_API_URL}/${getResellerIdFromRedux()}/pulse-internal-tickets`;

export const getPulseInternalTickets = async (params: InternalSupportTicketListParams = {}) => {
    const { data } = await http.get(getBaseUrl(), { params });

    return data as InternalSupportTicketListResponse;
};

export const createPulseInternalTicket = async (payload: CreateInternalSupportTicketPayload) => {
    const formData = new FormData();

    formData.append('title', payload.title);
    formData.append('description', payload.description || '');
    formData.append('priority', payload.priority);

    payload.assignees?.forEach((assignee) => {
        formData.append('assignees[]', assignee);
    });

    if (payload.dueDate) {
        formData.append('dueDate', payload.dueDate);
    }

    payload.attachmentFiles?.forEach((file) => {
        formData.append('attachments', file);
    });

    const { data } = await http.post(getBaseUrl(), formData);

    return data as InternalSupportTicket;
};

export const getPulseInternalTicketById = async (ticketId: string) => {
    const { data } = await http.get(`${getBaseUrl()}/${ticketId}`);

    return data as InternalSupportTicket;
};

export const updatePulseInternalTicket = async (ticketId: string, payload: UpdateInternalSupportTicketPayload) => {
    const { data } = await http.patch(`${getBaseUrl()}/${ticketId}`, payload);

    return data as InternalSupportTicket;
};

export const updatePulseInternalTicketStatus = async (ticketId: string, status: TicketStatus) => {
    const { data } = await http.patch(`${getBaseUrl()}/${ticketId}/status`, { status });

    return data as InternalSupportTicket;
};

export const updatePulseInternalTicketPriority = async (ticketId: string, priority: TicketPriority) => {
    const { data } = await http.patch(`${getBaseUrl()}/${ticketId}/priority`, { priority });

    return data as InternalSupportTicket;
};

export const addPulseInternalTicketAssignee = async (ticketId: string, userId: string) => {
    const { data } = await http.post(`${getBaseUrl()}/${ticketId}/assignees`, { userId });

    return data as InternalSupportTicket;
};

export const removePulseInternalTicketAssignee = async (ticketId: string, userId: string) => {
    const { data } = await http.delete(`${getBaseUrl()}/${ticketId}/assignees/${userId}`);

    return data as InternalSupportTicket;
};

export const getPulseInternalTicketMessages = async (ticketId: string) => {
    const { data } = await http.get(`${getBaseUrl()}/${ticketId}/messages`);

    return data as InternalSupportMessageListResponse;
};

export const createPulseInternalTicketMessage = async (
    ticketId: string,
    payload: CreateInternalSupportMessagePayload
) => {
    const formData = new FormData();

    if (payload.message) {
        formData.append('message', payload.message);
    }

    formData.append('type', payload.type);

    payload.mentions?.forEach((mentionId) => {
        formData.append('mentions[]', mentionId);
    });

    payload.attachmentFiles?.forEach((file) => {
        formData.append('attachments[]', file);
    });

    const { data } = await http.post(`${getBaseUrl()}/${ticketId}/messages`, formData);

    return data as InternalSupportMessage;
};

export const getPulseInternalTicketActivity = async (ticketId: string) => {
    const { data } = await http.get(`${getBaseUrl()}/${ticketId}/activity`);

    return data as InternalSupportActivityListResponse;
};

export const getAssignableUsers = async () => {
    const { data } = await http.get(`${getBaseUrl()}/assignable-users`);

    return data as InternalSupportUser[];
};
